"use client";

import Link from "next/link";
import { useState } from "react";
import { CreditCard, LogOut, Settings, User } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { APP_THEMES } from "@/lib/constants/themes";
import { cn } from "@/lib/utils";
import {
  MENU_HEADER,
  MENU_ITEM,
  MENU_ITEM_DESTRUCTIVE,
  MENU_ITEM_ICON,
  MENU_SECTION,
} from "@/components/ui/menu-styles";

/** "Tim Burnham" -> "TB", "halle" -> "H", falling back to the email's first letter. */
// eslint-disable-next-line react-refresh/only-export-components
export function initialsOf(displayName: string, email?: string | null): string {
  const parts = displayName.trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  if (parts.length === 1 && parts[0]) return parts[0][0].toUpperCase();
  return (email?.[0] ?? "?").toUpperCase();
}

interface AccountMenuProps {
  displayName: string;
  email?: string | null;
  /** Profile photo, when the account has one — otherwise the initials avatar shows. */
  avatarUrl?: string | null;
  /** Gating mirrors AdminSidebar so the menu never offers a page the sidebar hides. */
  isOwner: boolean;
  isAdmin: boolean;
  isPlatformAdmin: boolean;
  onSignOut: () => void;
}

/**
 * The whole account surface in one avatar: profile, billing and sign-out live
 * behind it so the home page's top bar stays a school name and a face.
 */
export function AccountMenu({
  displayName,
  email,
  avatarUrl,
  isOwner,
  isAdmin,
  isPlatformAdmin,
  onSignOut,
}: AccountMenuProps) {
  const [open, setOpen] = useState(false);
  const initials = initialsOf(displayName, email);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          aria-label="Account menu"
          className={cn(
            "flex items-center justify-center h-10 w-10 rounded-full overflow-hidden shrink-0",
            "ring-2 ring-white/70 shadow-sm transition-all duration-200 hover:shadow-md active:scale-95 focus:outline-none",
            !avatarUrl && "bg-gradient-to-br from-purple-600 to-orange-500",
          )}
        >
          {avatarUrl ? (
            <img src={avatarUrl} alt="" className="h-full w-full object-cover" />
          ) : (
            <span className="text-sm font-semibold text-white tracking-wide">{initials}</span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        sideOffset={8}
        className="w-60 p-1.5 rounded-lg border-gray-200 shadow-lg"
        style={APP_THEMES.settings.cssVars as React.CSSProperties}
      >
        <div className={MENU_HEADER}>
          <p className="text-sm font-semibold text-gray-900 truncate">{displayName}</p>
          {email && <p className="text-xs text-gray-500 truncate">{email}</p>}
        </div>
        <Link href="/settings" onClick={() => setOpen(false)} className={MENU_ITEM}>
          <User className={MENU_ITEM_ICON} />
          My Account
        </Link>
        {(isAdmin || isPlatformAdmin) && (
          <Link href="/settings/schools" onClick={() => setOpen(false)} className={MENU_ITEM}>
            <Settings className={MENU_ITEM_ICON} />
            Schools &amp; Settings
          </Link>
        )}
        {(isOwner || isPlatformAdmin) && (
          <Link href="/settings/subscriptions" onClick={() => setOpen(false)} className={MENU_ITEM}>
            <CreditCard className={MENU_ITEM_ICON} />
            Subscription
          </Link>
        )}
        <div className={MENU_SECTION}>
          <button
            onClick={() => {
              setOpen(false);
              onSignOut();
            }}
            className={cn(MENU_ITEM, MENU_ITEM_DESTRUCTIVE)}
          >
            <LogOut className="h-4 w-4" />
            Sign Out
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
